import { EventSession, Id, ReEvent } from '../../interfaces/types';
import { FC, useEffect, useState } from "react";
import { ClearOutlined, DeleteForever } from "@mui/icons-material";
import { Box, Button, Input, styled, Typography } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCustomThemeContext } from '../../providers/customThemeProvider';
import { useParams } from 'react-router-dom';
import { useEventContext } from '../../providers/EventProvider';

const StyledBox =styled(Box)({
    backgroundColor: "red",
});

interface Props {
    eventSession: EventSession;
    deleteEventSession: (eventSessionId: Id) => void;
    updateSessionTitle: (eventSessionId: Id, title: string) => void;
    updateSessionDuration: (eventSessionId: Id, duration: number) => void;
}

const EventSessionContainer: FC<Props> = (props) => {
  const { theme } = useCustomThemeContext();
    const {eventSession, deleteEventSession, updateSessionTitle, updateSessionDuration} = props;
    const [mouseIsOver, setMouseIsOver] = useState(false);
    const [editTitleMode, setEditTitleMode] = useState(false);
    const [editDurationMode, setEditDurationMode] = useState(false);
    const {eventId} = useParams();
    const {getEventById} = useEventContext();
    const [currentEvent, setCurrentEvent] = useState<ReEvent>();

    useEffect(() => {
      if(eventId) {
        const eventToSet = getEventById(eventId);
        if(eventToSet) setCurrentEvent(eventToSet);
      }
    }, [eventId, getEventById]);


    function formatTime(date: Date): string {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    }

    const {setNodeRef, attributes, listeners,
        transform, transition, isDragging} = useSortable({
        id: eventSession.id,
        data:{
            type: "EventSession",
            eventSession,
        },
        disabled: editTitleMode || editDurationMode,
    });

    const style = {
        transition,
        transform: CSS.Transform.toString(transform),
    };
    
    if(isDragging){
        return <Box className="DraggedEventSessionContainer"
        ref={setNodeRef}
        style={style}
        border={2}
        borderColor={theme.palette.dragBorderColor.main}
        minHeight={"90px"}
        margin={1}
        ></Box>;
    }

  return (
    <Box 
        bgcolor={eventSession.isBreak? theme.palette.secondary.main : theme.palette.tertiary.main}
        borderRadius={2}
        padding={1}
        margin={1}
        textAlign={"left"}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="EventSessionContainer"
        onMouseEnter={() => setMouseIsOver(true)}
        onMouseLeave={() => setMouseIsOver(false)}>
        <Box sx={{width: "100%", display: "flex", flexDirection: "column", alignItems: "left"}} >
            <Typography>{formatTime(eventSession.startTime)}</Typography>
            <Box sx={{width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between"}}>
                <Box className="SessionTitle"
                        onClick={() => {setEditTitleMode(true)}}
                        sx={{ml:1, mr:1}}>
                    {!editTitleMode && <Typography variant="h6">{eventSession.title}</Typography>}
                    {editTitleMode && <Input autoFocus
                        value={eventSession.title}
                        onBlur={() => setEditTitleMode(false)}
                        onKeyDown={(e) => {
                            if(e.key !== "Enter") return;
                            setEditTitleMode(false);
                        }
                    }
                    onChange={(e) => (updateSessionTitle(eventSession.id, e.target.value))}
                    />}
                </Box>
                <Box className="SessionDuration"
                        onClick={() => {setEditDurationMode(true)}}
                        sx={{ml:1, mr:1}}>
                    {!editDurationMode && <Typography variant="h6">{currentEvent?.eventConfiguration.possibleDurations.find(duration => duration.id === eventSession.duration)?.duration || eventSession.duration}min</Typography>}
                    {editDurationMode && <Input autoFocus
                        type="number"
                        value={eventSession.duration}
                        onBlur={() => setEditDurationMode(false)}
                        onKeyDown={(e) => {
                            if(e.key !== "Enter") return;
                            setEditDurationMode(false);
                        }
                    }
                    onChange={(e) => (updateSessionDuration(eventSession.id, Number(e.target.value)))}
                    />}
                </Box>
                
                {mouseIsOver && eventSession.roomId !== "unassigned" &&
                <Button variant="outlined" 
                    color="error" 
                    size="small"
                    onClick={() => {deleteEventSession(eventSession.id)}}>
                    <ClearOutlined/>
                </Button>}
                {mouseIsOver && eventSession.roomId === "unassigned" && (eventSession.isBreak || eventSession.isCopy) &&
                <Button variant="outlined" 
                    color="error" 
                    size="small"
                    onClick={() => {deleteEventSession(eventSession.id)}}>
                    <DeleteForever/>
                </Button>}
            </Box>
            <Typography>{formatTime(eventSession.endTime)}</Typography>
        </Box>
    </Box>
  )
}

export default EventSessionContainer;